import type { CSSProperties, ReactNode } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';

/**
 * 글자 앞뒤에 붙는 작은 기호(› ＋ ✓ ○)를 글자 대신 SVG 로 그리는 공용 부품(UI 가이드 4.1).
 * 글자 기호는 글꼴 기준선 때문에 글자 줄 가운데에서 벗어나고 글꼴마다 굵기·크기가 달라진다.
 *
 * - `IconLabel`   — [아이콘] 글자 [아이콘]. inline-flex + align-items:center 로 아이콘 세로 중심 = 글자 줄 세로 중심
 * - `ChevronLabel` — 글자 + 16px 오른쪽 꺾쇠(링크 "지도에서 보기 ›", "더보기 ›" 등)
 * - `InlineIcon`  — 아이콘 하나(display:block, 크기 고정) — 이미 flex 인 칸 안에 직접 넣을 때
 */
/** heart(좋아요 수 앞 ♡)·star(별점 앞 ⭐) — 2026-10-11 글자 기호·이모지를 SVG 로(C9) */
export type InlineIconKind = 'chevron' | 'add' | 'check' | 'circle' | 'heart' | 'star';

const ICONS: Record<InlineIconKind, typeof AddRoundedIcon> = {
    chevron: ChevronRightRoundedIcon,
    add: AddRoundedIcon,
    check: CheckRoundedIcon,
    circle: RadioButtonUncheckedRoundedIcon,
    heart: FavoriteBorderRoundedIcon,
    star: StarRoundedIcon,
};

export const InlineIcon = ({ kind, size = 16 }: { kind: InlineIconKind; size?: number }) => {
    const Icon = ICONS[kind];
    return (
        <Icon
            aria-hidden="true"
            style={{ display: 'block', flex: `0 0 ${size}px`, width: size, height: size, fontSize: size }}
        />
    );
};

export interface IconLabelProps {
    children: ReactNode;
    /** 글자 앞 아이콘 */
    start?: InlineIconKind;
    /** 글자 뒤 아이콘 */
    end?: InlineIconKind;
    /** 아이콘 크기(px) — 글자 뒤 꺾쇠 16, 칩 안 체크 12~14 */
    size?: number;
    /** 아이콘과 글자 사이(px) */
    gap?: number;
    /** 칸이 좁으면 글자만 말줄임(아이콘은 남긴다) */
    truncate?: boolean;
    style?: CSSProperties;
}

const truncateStyle: CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

export const IconLabel = ({ children, start, end, size = 16, gap = 2, truncate = false, style }: IconLabelProps) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap,
            maxWidth: '100%',
            minWidth: 0,
            verticalAlign: 'top',
            ...style,
        }}
    >
        {start && <InlineIcon kind={start} size={size} />}
        {truncate ? <span style={truncateStyle}>{children}</span> : children}
        {end && <InlineIcon kind={end} size={size} />}
    </span>
);

/**
 * 글자 + 16px 오른쪽 꺾쇠 — 이동 링크("지도에서 보기", "더보기", "사용하러") 공용.
 * 꺾쇠 SVG 는 왼쪽에 약 6px 빈 칸이 있어 간격 0 으로 붙여도 글자와 꺾쇠 사이가 예전 "글자 ›"(띄어쓰기 한 칸) 정도로 보인다.
 */
export const ChevronLabel = ({ gap = 0, ...props }: Omit<IconLabelProps, 'end' | 'start'>) => <IconLabel end="chevron" gap={gap} {...props} />;

export default IconLabel;
